/**
 * Disciplinary API hooks — TanStack Query layer over /api/disciplinary.
 *
 * Admin/Manager can issue/edit actions; employees see only their own.
 * Separation of duties: cannot issue/edit a disciplinary action about self.
 * Employees acknowledge their own actions via POST /:id/acknowledge.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../../lib/api.js';
import type { DisciplinaryAction, Paginated } from '@shared/types';
import {
  disciplinaryCreateInput,
  disciplinaryUpdateInput,
} from '@shared/schemas/index.js';
import { z } from 'zod';

export type DisciplinaryCreateBody = z.input<typeof disciplinaryCreateInput>;
export type DisciplinaryUpdateBody = z.input<typeof disciplinaryUpdateInput>;

export const disciplinaryKeys = {
  all: ['disciplinary'] as const,
  list: (params?: { employeeId?: string }) =>
    [...disciplinaryKeys.all, 'list', params ?? {}] as const,
};

/** List disciplinary actions, optionally scoped to an employee. */
export function useDisciplinary(params?: { employeeId?: string }) {
  return useQuery({
    queryKey: disciplinaryKeys.list(params),
    queryFn: () =>
      api.get<Paginated<DisciplinaryAction>>(`/api/disciplinary${qs(params)}`),
  });
}

/** Create a disciplinary action (Admin/Manager; not about self). */
export function useCreateDisciplinary() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: DisciplinaryCreateBody) =>
      api.post<DisciplinaryAction>('/api/disciplinary', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: disciplinaryKeys.all });
    },
  });
}

/** Update a disciplinary action (Admin/Manager; not about self). */
export function useUpdateDisciplinary() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: DisciplinaryUpdateBody }) =>
      api.put<DisciplinaryAction>(`/api/disciplinary/${id}`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: disciplinaryKeys.all });
    },
  });
}

/** Acknowledge a disciplinary action (employee acknowledging their own). */
export function useAcknowledgeDisciplinary() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ ok: boolean }>(`/api/disciplinary/${id}/acknowledge`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: disciplinaryKeys.all });
    },
  });
}

/** Delete a disciplinary action (Admin/Manager; not about self). */
export function useDeleteDisciplinary() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ ok: boolean }>(`/api/disciplinary/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: disciplinaryKeys.all });
    },
  });
}
